import config from '../config';

export type Brand = 'visa' | 'mastercard' | 'amex' | 'diners' | 'elo';

export type CreditCard = {
    errors: () => {
        number?: string;
        verification_value?: string;
        first_name?: string;
        last_name?: string;
        month?: string;
        year?: string;
        brand?: string;
    };
    valid: () => boolean;
    brand: () => Brand;
    toData: () => {
        number: string;
        verification_value: number;
        first_name: string;
        last_name: string;
        month: number;
        year: number;
        brand: Brand;
    };
};

export type PaymentToken = {
    id: string,
    method: 'credit_card',
    extra_info: {
        bin: string
        brand: string
        display_number: string
        holder_name: string
        month: number
        year: number
    },
    test: boolean
};

export type PaymentTokenError = {
    errors: { [key: string]: string }
} | {
    id: string,
    method: 'credit_card',
    extra_info: {
        bin: string
        brand: string
        display_number: string
        holder_name: string
        month: number
        year: number
    },
    test: boolean
};

export type PaymentTokenResponse = PaymentToken | PaymentTokenError;

let Iugu: {
    utils: {
        isBlockedByAdBlock: () => boolean,
        validateAccountID: (accountID: string) => string | void;
        formatUUID: (uuid: string) => string;
        validateCreditCardNumber: (cardNumber: string) => boolean;
        keyOf: <O extends object, V = any, K extends keyof O = keyof O>(object: O, value: V) => K | null;
        validateCVV: (cvv: string, brand: Brand) => boolean;
        validateExpiration: (month: number, year: number) => boolean;
        validateExpirationString: (date: string) => boolean;
        validateFirstName: (name: string) => boolean;
        validateLastName: (name: string) => boolean;
        getMonthYearByFullExpiration: (date: string) => false | [string, string];
        getFirstLastNameByFullName: (name: string) => false | [string, string];
        getBrandByCreditCardNumber: (cardNumber: string) => Brand;
    };
    CreditCard: (cardNumber: string, month: string, year: string, firstName: string, lastName: string, cvv: string) => CreditCard;
    setup: () => void;
    createPaymentToken: (creditCard: CreditCard, callback: (response: PaymentTokenResponse) => void) => void;
    setAccountID: (id: string) => void;
    getSessionId: () => string;
    setTestMode: (isTest: boolean) => void;
} = {} as any;

let loader: Promise<void> | null = null;

const load = () => {
    if (loader === null)
        loader = new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');

            script.src = 'https://js.iugu.com/v2';

            script.async = true;

            script.onload = () => {
                Iugu = (window as any).Iugu;

                Iugu.setAccountID("8525D7E7C15D42E194705E12A804B002");

                Iugu.setTestMode(!config.production);

                resolve();
            };

            script.onerror = reject;

            document.body.appendChild(script);
        });

    return loader;
};

const isBlockedByAdBlock = () => {
    const timer = performance.now();

    return timer >= 5000
        ? Promise.resolve(Iugu.utils.isBlockedByAdBlock())
        : new Promise<boolean>(resolve => setTimeout(() => resolve(Iugu.utils.isBlockedByAdBlock()), 5000 - timer));
};

const createPaymentToken = (creditCard: CreditCard) => new Promise<PaymentToken>(
    (resolve, reject) => Iugu.createPaymentToken(
        creditCard,
        response => 'errors' in response ? reject(response.errors) : resolve(response)
    )
);

const payment = {
    load,
    get Iugu() {
        return { ...Iugu, createPaymentToken, utils: { ...Iugu.utils, isBlockedByAdBlock } };
    }
};

export default payment;
